import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { trpc, trpcUtils } from "@/hooks/trpc";
import { HarvestProtocolId } from "@brewbase/db/types";
import { z } from "zod";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import { Wheat } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/harvests/protocols/$protocol_id")({
  component: RouteComponent,
  params: z.object({
    protocol_id: z.coerce
      .number()
      .transform((val) => val as HarvestProtocolId),
  }),
  loader: async ({ params }) => {
    await trpcUtils.harvest.protocols.getWithFullDetails.ensureData(
      params.protocol_id,
    );
  },
});

function RouteComponent() {
  const { protocol_id } = Route.useParams();
  const { data: protocol, isLoading } =
    trpc.harvest.protocols.getWithFullDetails.useQuery(protocol_id);

  const isDraft = protocol?.harvest_session_id != null;

  useSidebarConfig({
    layer: 2,
    parent: isDraft && protocol?.harvest_session_id
      ? {
          label: "Session",
          icon: Wheat,
          href: `/harvests/${protocol.harvest_session_id}`,
        }
      : { label: "Protocols", icon: Wheat, href: "/harvests/protocols" },
    header: {
      title: protocol?.name ?? (isDraft ? "Untitled draft" : "Protocol"),
      subtitle: isDraft
        ? "Draft"
        : protocol?.version
          ? `v${protocol.version}`
          : undefined,
      actions: isDraft ? <PublishDraftButton draftId={protocol_id} /> : undefined,
    },
    navigation: [
      {
        type: "link",
        id: "operations",
        label: "Operations",
        href: `/harvests/protocols/${protocol_id}`,
        exact: true,
      },
      {
        type: "link",
        id: "kpis",
        label: "KPIs",
        href: `/harvests/protocols/${protocol_id}/kpis`,
      },
      {
        type: "link",
        id: "cleaning",
        label: "Cleaning",
        href: `/harvests/protocols/${protocol_id}/cleaning`,
      },
    ],
  });

  if (isLoading || !protocol) {
    return (
      <div className="flex items-center justify-center h-full">
        Loading protocol...
      </div>
    );
  }

  return (
    <div className="h-full">
      <Outlet />
    </div>
  );
}

function PublishDraftButton({ draftId }: { draftId: HarvestProtocolId }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [version, setVersion] = useState("1.0.0");

  const publishMutation = trpc.harvest.protocols.publish.useMutation({
    onSuccess: (newProtocol) => {
      toast.success(`Published as ${newProtocol.name} v${newProtocol.version}`);
      utils.harvest.protocols.list.invalidate();
      utils.harvest.protocols.getWithFullDetails.invalidate(draftId);
      setOpen(false);
      navigate({
        to: "/harvests/protocols/$protocol_id",
        params: { protocol_id: newProtocol.id as HarvestProtocolId },
      });
    },
    onError: (error) => {
      toast.error(`Publish failed: ${error.message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Publish as Template
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Publish Draft</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="publish-name">Name</Label>
            <Input
              id="publish-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Bench: Continuous Harvest"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="publish-version">Version</Label>
            <Input
              id="publish-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 1.0.0"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              publishMutation.mutate({
                draftProtocolId: draftId,
                name,
                version,
              })
            }
            disabled={!name || !version || publishMutation.isPending}
          >
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
